import {Component, Input, OnChanges, OnDestroy, OnInit, Optional, SimpleChanges, ViewChild} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {forkJoin, interval, Observable, of, Subscription} from 'rxjs';
import {catchError, exhaustMap, map, startWith} from 'rxjs/operators';
import * as _ from 'lodash';
import {Router} from '@angular/router';
import {DomSanitizer, SafeUrl} from '@angular/platform-browser';
import {LocationPhotoService, LocationPhotoSlot} from 'app/core/installation/services/location-photo.service';
import {InstallationService} from '../../../../core/installation/services/installation.service';
import {AlarmService} from '../../../../core/alarm/services/alarm.service';
import {CurrentSensorData, SensorDataService} from '../../../../core/device/services/sensor-data.service';
import {InstallationMapComponent} from '../installation-map/installation-map.component';

interface DeviceTypeFilter {
  id: number;
  name: string;
  count: number;
  selected: boolean;
  icon: string;
}

interface DevicePanelMetric {
  label: string;
  value: string;
  health: string;
}

interface DeviceSensorGroup {
  key: string;
  label: string;
  icon: string;
  open: boolean;
  sensors: CurrentSensorData[];
}

@Component({
  selector: 'app-new-installation-map',
  templateUrl: './new-installation-map.component.html',
  styleUrls: ['./new-installation-map.component.scss']
})
export class NewInstallationMapComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild(InstallationMapComponent) mapComponent: InstallationMapComponent;
  @Input() embedded = false;
  @Input() embeddedHeight = '430px';
  // null: el mapa obtiene las alarmas; []: el padre informa que no existen alarmas.
  @Input() alarmList: any[] | null = null;

  installations: any[] = [];
  visibleInstallations: any[] = [];
  alarms: any[] = [];
  search = '';
  filtersOpen = true;
  layersOpen = false;
  mapMode: 'roadmap' | 'satellite' = 'satellite';
  panelTab: 'summary' | 'events' | 'status' = 'summary';
  deviceTypes: DeviceTypeFilter[] = [];
  selectedLocation: any = null;
  selectedDevice: any = null;
  currentSensors: CurrentSensorData[] = [];
  healthIndicators: DevicePanelMetric[] = [];
  sensorGroups: DeviceSensorGroup[] = [];
  sensorsLoading = false;
  loaded = false;
  installationsError = false;
  alarmsError = false;
  lastRefresh = new Date();
  refreshCountdown = 5;
  layers = {
    pins: true,
    labels: true,
    zones: true,
    alarms: true
  };

  readonly statusMap = {
    1: 'APAGADO',
    2: 'ARMADO',
    3: 'DES-ARMADO',
    4: 'ALARMADO',
    5: 'BLOQUEADO'
  };

  readonly legend = [
    {name: 'Bloqueado', color: '#3159d9'},
    {name: 'Armado', color: '#14a875'},
    {name: 'Des-armado', color: '#e2c52e'},
    {name: 'Alarmado', color: '#ef4747'},
    {name: 'Apagado', color: '#6d7882'}
  ];

  /** Fotos de instalación de la ubicación seleccionada (carrusel de "Activo monitoreado"). */
  readonly PHOTO_SLOTS: LocationPhotoSlot[] = [1, 2];
  photoSlides: Array<{ photo: LocationPhotoSlot; url: SafeUrl }> = [];
  photoIndex = 0;
  photosLoading = false;
  private photoObjectUrls: string[] = [];
  private photoLocationId: number | null = null;

  private subscriptions: Subscription[] = [];
  private destroyed = false;
  private pendingTimeouts = new Set<ReturnType<typeof setTimeout>>();

  constructor(
    @Optional() public activeModal: NgbActiveModal,
    private installationService: InstallationService,
    private alarmService: AlarmService,
    private sensorDataService: SensorDataService,
    private router: Router,
    private locationPhotoService: LocationPhotoService,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    if (this.destroyed) return;
    if (!this.embedded) document.body.classList.add('siac-map-fullscreen-open');
    if (this.embedded) this.filtersOpen = false;
    // El carrusel de fotos avanza solo cada 6 s, como en el modal del activo.
    this.subscriptions.push(interval(6000).subscribe(() => {
      if (this.photoSlides.length > 1) this.showNextPhoto();
    }));

    this.subscriptions.push(
      interval(5000).pipe(
        startWith(0),
        exhaustMap(() => forkJoin([
          this.installationService.getAll().pipe(catchError(() => of(null))),
          (this.alarmList !== null ? of(this.alarmList) : this.loadActiveAlarms()).pipe(catchError(() => of(null)))
        ]))
      ).subscribe(([installations, alarms]) => {
        if (this.destroyed) return;
        this.installationsError = installations === null;
        this.alarmsError = alarms === null;
        if (installations !== null) {
          this.installations = (installations || []).filter(installation =>
            (installation.Locations || []).some(location => Number(location.status_id) === 1)
          );
        }
        if (alarms !== null) this.alarms = alarms || [];
        this.lastRefresh = new Date();
        this.refreshCountdown = 5;
        this.syncDeviceTypes();
        this.rebuildVisibleInstallations();
        this.loaded = true;
        this.refreshSelection();
      })
    );

    this.subscriptions.push(
      interval(1000).subscribe(() => {
        if (this.destroyed) return;
        const elapsed = Math.floor((Date.now() - this.lastRefresh.getTime()) / 1000);
        this.refreshCountdown = Math.max(0, 5 - elapsed);
      })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.destroyed) return;
    if (changes.alarmList && changes.alarmList.currentValue !== null) {
      this.alarmsError = false;
      this.alarms = Array.isArray(changes.alarmList.currentValue) ? changes.alarmList.currentValue : [];
      if (this.loaded) {
        this.rebuildVisibleInstallations();
        this.refreshSelection();
      }
    }
  }

  /**
   * @brief Carga una ventana reciente de alarmas activas para el mapa.
   *
   * La consulta usa el modo simple de la API porque el mapa solo necesita
   * relacionar cada alarma con su ubicación y dispositivo.
   *
   * @returns {Observable<any[]>} Alarmas activas sin duplicados.
   */
  private loadActiveAlarms(): Observable<any[]> {
    // En pantalla completa se carga una ventana reciente y acotada. El total
    // histórico no debe recorrerse para poder dibujar el mapa.
    return this.alarmService.getPage({
      sortField: 'id',
      sortOrder: 'desc',
      pageSize: 100,
      pageNumber: 0,
      filter: {
        alarm_status_id: 1,
        simple: true
      }
    } as any).pipe(
      map((response: any) => {
        const pageItems = response && Array.isArray(response.items) ? response.items : [];
        return Array.from(new Map(pageItems.map(alarm => [String(alarm.id), alarm])).values());
      })
    );
  }

  private getAllLocations(): any[] {
    return _.flatten((this.installations || []).map(installation =>
      (installation.Locations || []).filter(location => Number(location.status_id) === 1).map(location => ({
        ...location,
        Installation: location.Installation || installation,
        installation: location.installation || installation,
        installation_name: location.installation_name || installation.name
      }))
    ));
  }

  private getLocationDevices(location: any): any[] {
    const devices = location && (Array.isArray(location.Devices) ? location.Devices : location.devices);
    return Array.isArray(devices) ? devices.filter(Boolean) : [];
  }

  private syncDeviceTypes(): void {
    const previous = new Map(this.deviceTypes.map(type => [type.id, type.selected]));
    const values = new Map<number, {name: string; count: number}>();

    this.getAllLocations().forEach(location => this.getLocationDevices(location).forEach(device => {
      const id = Number(device.device_type_id || _.get(device, 'DeviceType.id'));
      if (!Number.isFinite(id)) return;
      const current = values.get(id) || {name: this.getDeviceTypeName(device, id), count: 0};
      current.count++;
      values.set(id, current);
    }));

    this.deviceTypes = Array.from(values.entries()).map(([id, value]) => ({
      id,
      name: value.name,
      count: value.count,
      selected: previous.has(id) ? previous.get(id) : true,
      icon: this.getDeviceTypeIcon(id)
    })).sort((a, b) => a.name.localeCompare(b.name));
  }

  get filteredLocations(): any[] {
    const query = (this.search || '').trim().toLocaleLowerCase();
    const selectedTypes = new Set(this.deviceTypes.filter(type => type.selected).map(type => type.id));

    return this.getAllLocations().filter(location => {
      const devices = this.getLocationDevices(location);
      // Las ubicaciones sin dispositivos conservan su pin y no dependen del
      // filtro de tipos de dispositivo.
      const typeMatches = !devices.length || !this.deviceTypes.length || devices.some(device =>
        selectedTypes.has(Number(device.device_type_id || _.get(device, 'DeviceType.id')))
      );
      if (!typeMatches) return false;
      if (!query) return true;

      return [
        location.name,
        location.description,
        location.internal_code,
        location.address,
        location.installation_name,
        ...devices.reduce((values, device) => values.concat([
          device.name,
          device.description,
          device.internal_code,
          device.mac
        ]), [])
      ].filter(Boolean).join(' ').toLocaleLowerCase().includes(query);
    });
  }

  private rebuildVisibleInstallations(): void {
    const ids = new Set(this.filteredLocations.map(location => location.id));
    this.visibleInstallations = this.installations.map(installation => ({
      ...installation,
      Locations: (installation.Locations || []).filter(location => ids.has(location.id))
    })).filter(installation => installation.Locations.length);
  }

  get installationCount(): number {
    return this.visibleInstallations.length;
  }

  get deviceCount(): number {
    return this.filteredLocations.reduce((total, location) => total + this.getLocationDevices(location).length, 0);
  }

  get allTypesSelected(): boolean {
    return this.deviceTypes.length > 0 && this.deviceTypes.every(type => type.selected);
  }

  get selectedTypeCount(): number {
    return this.deviceTypes.filter(type => type.selected).length;
  }

  get alarmsInView(): any[] {
    const ids = new Set(this.filteredLocations.map(location => Number(location.id)));
    return (this.alarms || []).filter(alarm => ids.has(this.getAlarmLocationId(alarm)));
  }

  get latestAlarms(): any[] {
    if (!this.layers.alarms) return [];
    return this.alarmsInView.slice().sort((a, b) =>
      new Date(b.create_date).getTime() - new Date(a.create_date).getTime()
    ).slice(0, 3);
  }

  get selectedDeviceAlarms(): any[] {
    if (!this.selectedDevice) return [];
    return (this.alarms || []).filter(alarm =>
      Number(alarm.device_id || _.get(alarm, 'Device.id')) === Number(this.selectedDevice.id)
    ).sort((a, b) => new Date(b.create_date).getTime() - new Date(a.create_date).getTime());
  }

  toggleAllTypes(): void {
    const value = !this.allTypesSelected;
    this.deviceTypes.forEach(type => type.selected = value);
    this.filtersChanged();
  }

  toggleType(type: DeviceTypeFilter): void {
    type.selected = !type.selected;
    this.filtersChanged();
  }

  filtersChanged(): void {
    this.rebuildVisibleInstallations();
    if (this.selectedLocation && !this.filteredLocations.some(location => location.id === this.selectedLocation.id)) {
      this.closeSelection();
    }
  }

  applySearch(): void {
    if (this.destroyed) return;
    this.filtersChanged();
    this.scheduleTimeout(() => this.fitVisibleLocations());
  }

  fitVisibleLocations(): void {
    if (this.destroyed) return;
    if (!this.mapComponent || !this.mapComponent.markers || !this.mapComponent.markers.length) return;
    this.mapComponent.setInitialBounds();
  }

  setMapType(type: 'roadmap' | 'satellite'): void {
    this.mapMode = type;
    if (this.mapComponent && this.mapComponent.map && this.mapComponent.map.googleMap) {
      this.mapComponent.map.googleMap.setMapTypeId(type);
    }
  }

  zoom(delta: number): void {
    if (!this.mapComponent || !this.mapComponent.map || !this.mapComponent.map.googleMap) return;
    const map = this.mapComponent.map.googleMap;
    map.setZoom((map.getZoom() || 1) + delta);
  }

  toggleLayer(layer: 'pins' | 'labels' | 'zones' | 'alarms'): void {
    this.layers[layer] = !this.layers[layer];
  }

  /**
   * @brief Selecciona una ubicación y el dispositivo que debe mostrarse.
   * @param {any} selection - Ubicación o evento `{location, device}` emitido por el mapa.
   * @param {any} [preferredDevice] - Dispositivo preferido, si existe.
   */
  selectLocation(selection: any, preferredDevice?: any): void {
    const location = selection && selection.location ? selection.location : selection;
    const selectedDevice = preferredDevice || (selection && selection.location ? selection.device : null);
    if (!location) return;

    if (this.embedded && this.mapComponent) {
      // En la vista normal el pin abre su modal funcional inmediatamente.
      this.mapComponent.selectionMode = false;
      this.mapComponent.locationClicked(location, selectedDevice || null);
      this.mapComponent.selectionMode = true;
      return;
    }

    this.selectedLocation = location;
    this.loadLocationPhotos(Number(location.id));
    const devices = this.getLocationDevices(location);
    const alarmed = devices.find(device => (this.alarms || []).some(alarm =>
      Number(alarm.device_id || _.get(alarm, 'Device.id')) === Number(device.id)
    ));
    const active = devices.find(device => Number(device.siac_status_id) === 2);
    this.selectDevice(selectedDevice || alarmed || active || devices[0] || null);
    this.panelTab = 'summary';
  }

  selectDeviceById(deviceId: number): void {
    const device = this.getLocationDevices(this.selectedLocation)
      .find(item => Number(item.id) === Number(deviceId));
    this.selectDevice(device || null);
  }

  private selectDevice(device: any): void {
    this.selectedDevice = device;
    this.currentSensors = [];
    this.buildDevicePanelData();
    if (!device || !device.id) return;

    this.sensorsLoading = true;
    this.sensorDataService.getCurrentSensorData({deviceId: Number(device.id)}).pipe(
      catchError(() => of({message: '', itemList: [] as CurrentSensorData[]}))
    ).subscribe(response => {
      if (this.destroyed) return;
      if (this.selectedDevice && Number(this.selectedDevice.id) === Number(device.id)) {
        this.currentSensors = response && Array.isArray(response.itemList) ? response.itemList : [];
        this.buildDevicePanelData();
        this.sensorsLoading = false;
      }
    });
  }

  private refreshSelection(): void {
    if (!this.selectedLocation) return;
    const location = this.getAllLocations().find(item => Number(item.id) === Number(this.selectedLocation.id));
    if (!location) return this.closeSelection();
    const device = this.getLocationDevices(location)
      .find(item => this.selectedDevice && Number(item.id) === Number(this.selectedDevice.id));
    this.selectedLocation = location;
    if (device) this.selectedDevice = device;
  }

  closeSelection(): void {
    this.selectedLocation = null;
    this.selectedDevice = null;
    this.currentSensors = [];
    this.buildDevicePanelData();
    this.loadLocationPhotos(null);
  }

  /**
   * @brief Carga las fotos de instalación (installation_photo_1/2) de la ubicación para el carrusel.
   * Solo vuelve a pedirlas cuando cambia la ubicación seleccionada.
   * @param {number | null} locationId - Ubicación seleccionada, o null para vaciar el carrusel.
   */
  private loadLocationPhotos(locationId: number | null): void {
    if (locationId && locationId === this.photoLocationId) return;
    this.photoLocationId = locationId || null;
    this.setPhotoSlides([]);
    if (!locationId) return;

    this.photosLoading = true;
    this.subscriptions.push(forkJoin(this.PHOTO_SLOTS.map(photo =>
      this.locationPhotoService.getPhoto(locationId, photo).pipe(catchError(() => of(null)))
    )).subscribe((blobs: Array<Blob | null>) => {
      // Descarta la respuesta si mientras cargaba se eligió otra ubicación.
      if (this.destroyed || this.photoLocationId !== locationId) return;
      this.photosLoading = false;
      this.setPhotoSlides(this.PHOTO_SLOTS
        .map((photo, index) => ({ photo, blob: blobs[index] }))
        .filter((slide): slide is { photo: LocationPhotoSlot; blob: Blob } => !!slide.blob));
    }));
  }

  private setPhotoSlides(slides: Array<{ photo: LocationPhotoSlot; blob: Blob }>): void {
    this.revokePhotoUrls();
    this.photoSlides = slides.map(slide => {
      const url = URL.createObjectURL(slide.blob);
      this.photoObjectUrls.push(url);
      // Angular marcaría las URLs blob: como inseguras sin este bypass; el contenido proviene de la API.
      return { photo: slide.photo, url: this.sanitizer.bypassSecurityTrustUrl(url) };
    });
    this.photoIndex = 0;
  }

  private revokePhotoUrls(): void {
    this.photoObjectUrls.forEach(url => URL.revokeObjectURL(url));
    this.photoObjectUrls = [];
  }

  showNextPhoto(): void {
    if (!this.photoSlides.length) return;
    this.photoIndex = (this.photoIndex + 1) % this.photoSlides.length;
  }

  showPreviousPhoto(): void {
    if (!this.photoSlides.length) return;
    this.photoIndex = (this.photoIndex - 1 + this.photoSlides.length) % this.photoSlides.length;
  }

  /**
   * @brief Abre el listado de alarmas conservando el ID y la fecha seleccionados.
   * @param {any} alarm - Alarma que se quiere consultar en el listado.
   */
  openAlarm(alarm: any): void {
    if (!alarm || !alarm.id) return;
    const date = new Date(alarm.create_date);
    const dateParam = isNaN(date.getTime()) ? null : [
      date.getFullYear(),
      String(date.getMonth() + 1).padStart(2, '0'),
      String(date.getDate()).padStart(2, '0')
    ].join('-');
    this.close();
    this.router.navigate(['/alarms'], {
      queryParams: {alarmId: alarm.id, date: dateParam}
    });
  }

  /**
   * @brief Abre el detalle completo de la ubicación seleccionada.
   */
  openDetail(): void {
    if (!this.selectedLocation || !this.mapComponent) return;
    this.mapComponent.selectionMode = false;
    this.mapComponent.locationClicked(this.selectedLocation, this.selectedDevice || null);
    this.mapComponent.selectionMode = true;
  }

  goToAlarms(): void {
    this.close();
    this.router.navigate(['/alarms'], {queryParams: {status: 'active'}});
  }

  close(): void {
    if (this.activeModal) this.activeModal.dismiss('close new installation map');
  }

  /**
   * @brief Obtiene el nombre del tipo de dispositivo para los filtros del mapa.
   * @param {any} device - Dispositivo recibido desde la API.
   * @param {number} id - ID del tipo de dispositivo.
   * @returns {string} Nombre del tipo o una etiqueta genérica.
   */
  getDeviceTypeName(device: any, id: number): string {
    const apiName = _.get(device, 'DeviceType.name') || _.get(device, 'device_type.name');
    const names = {3: 'PIR', 6: 'Infrarrojos', 7: 'Acelerómetros', 8: 'Gateways', 9: 'Cámaras', 10: 'Apertura'};
    return apiName || names[id] || `Tipo ${id}`;
  }

  private getDeviceTypeIcon(id: number): string {
    const icons = {3: 'fa fa-bullseye', 6: 'fa fa-eye', 7: 'fa fa-tachometer', 8: 'fa fa-wifi', 9: 'fa fa-video-camera', 10: 'fa fa-unlock-alt'};
    return icons[id] || 'fa fa-map-marker';
  }

  getDeviceName(device: any): string {
    if (!device) return 'Sin dispositivo';
    return device.description || device.name || device.internal_code || device.mac || `Dispositivo ${device.id}`;
  }

  getStatus(device: any): string {
    return this.statusMap[Number(device && device.siac_status_id)] || 'SIN DATOS';
  }

  get devicePanelState(): string {
    if (this.selectedDeviceAlarms.length) return 'ALARMADO';
    if (this.currentSensors.some(sensor => sensor.health_status === 'bad')) return 'ADVERTENCIA';
    return this.selectedDevice ? 'NORMAL' : 'SIN DATOS';
  }

  get devicePanelStateDescription(): string {
    if (this.selectedDeviceAlarms.length) {
      return `${this.selectedDeviceAlarms.length} ${this.selectedDeviceAlarms.length === 1 ? 'alarma activa' : 'alarmas activas'}`;
    }
    if (this.currentSensors.some(sensor => sensor.health_status === 'bad')) return 'Hay indicadores fuera de rango';
    return this.selectedDevice ? 'Sin alarmas activas' : 'No hay un dispositivo asociado';
  }

  get selectedDeviceIdentifier(): string {
    if (!this.selectedDevice) return 'Sin identificador';
    return this.selectedDevice.name || this.selectedDevice.internal_code || this.selectedDevice.mac || `ID ${this.selectedDevice.id}`;
  }

  get selectedInstallationName(): string {
    return this.selectedLocation && (
      this.selectedLocation.installation_name ||
      _.get(this.selectedLocation, 'Installation.name') ||
      _.get(this.selectedLocation, 'installation.name')
    ) || 'Sin información';
  }

  get lastCommunicationLabel(): string {
    return this.formatRelativeDate(this.getLatestSensorDate());
  }

  focusSelectedLocation(): void {
    if (!this.selectedLocation || !this.mapComponent) return;
    this.mapComponent.panToLocationId(this.selectedLocation.id);
  }

  getSensorValue(sensor: CurrentSensorData): string {
    if (!sensor || sensor.value === null || sensor.value === undefined) return 'Sin datos';
    return `${sensor.value}${sensor.SensorType && sensor.SensorType.unit ? ' ' + sensor.SensorType.unit : ''}`;
  }

  getLatestSensorDate(): string | null {
    if (!this.currentSensors.length) return null;
    return this.currentSensors.slice().sort((a, b) =>
      new Date(b.create_date).getTime() - new Date(a.create_date).getTime()
    )[0].create_date;
  }

  getSensorLabel(sensor: CurrentSensorData): string {
    return sensor && (sensor.SensorType && (sensor.SensorType.description || sensor.SensorType.name))
      || `Sensor ${sensor && sensor.sensor_type_id || ''}`.trim();
  }

  getSensorHealth(sensor: CurrentSensorData): string {
    return sensor && sensor.health_status || 'unavailable';
  }

  formatRelativeDate(value: string | Date | null): string {
    if (!value) return 'Sin datos';
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) return 'Sin datos';
    const elapsedSeconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
    if (elapsedSeconds < 60) return 'Hace menos de 1 min';
    const minutes = Math.floor(elapsedSeconds / 60);
    if (minutes < 60) return `Hace ${minutes} min`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `Hace ${hours} h`;
    const days = Math.floor(hours / 24);
    return `Hace ${days} d`;
  }

  private buildDevicePanelData(): void {
    const definitions = [
      {id: 5, label: 'Voltaje de batería'},
      {id: 7, label: 'Señal (RSSI)'},
      {id: 33, label: 'Variación magnética'},
      {id: 32, label: 'Cambio de altura'},
      {id: 27, label: 'Estado acelerómetro'}
    ];
    this.healthIndicators = definitions.map(definition => {
      const sensor = this.currentSensors.find(item => Number(item.sensor_type_id) === definition.id);
      return {
        label: definition.label,
        value: sensor ? this.getSensorValue(sensor) : 'Sin datos',
        health: sensor ? this.getSensorHealth(sensor) : 'unavailable'
      };
    });

    const groups: DeviceSensorGroup[] = [
      {key: 'physical', label: 'Seguridad física', icon: 'fa-shield', open: true, sensors: []},
      {key: 'power', label: 'Alimentación y comunicación', icon: 'fa-battery-half', open: true, sensors: []},
      {key: 'position', label: 'Posición y GNSS', icon: 'fa-map-marker', open: false, sensors: []},
      {key: 'configuration', label: 'Configuración', icon: 'fa-cogs', open: false, sensors: []},
      {key: 'diagnostics', label: 'Diagnóstico y sistema', icon: 'fa-stethoscope', open: false, sensors: []}
    ];

    this.currentSensors.forEach(sensor => {
      const id = Number(sensor.sensor_type_id);
      const text = `${this.getSensorLabel(sensor)} ${sensor.SensorType && sensor.SensorType.identificator || ''}`.toLocaleLowerCase();
      let key = 'diagnostics';
      if ([24, 25, 26, 27, 32, 33].includes(id) || /aceler|movim|magn|tamper/.test(text)) key = 'physical';
      else if ([5, 7, 30].includes(id) || /bater|volt|rssi|mqtt|señal|senal|red|comunic/.test(text)) key = 'power';
      else if (id === 31 || /gps|gnss|latitud|longitud|posición|posicion|altura/.test(text)) key = 'position';
      else if (/config|versión|version|firmware/.test(text)) key = 'configuration';
      groups.find(group => group.key === key).sensors.push(sensor);
    });
    this.sensorGroups = groups;
  }

  getLocationImage(): string {
    if (!this.selectedLocation) return '';
    if (this.selectedLocation.image) return this.selectedLocation.image;
    try {
      return this.mapComponent ? this.mapComponent.getLocationImageUrl(this.selectedLocation) : 'assets/icons/dash/Vector29.svg';
    } catch (_) {
      return 'assets/icons/dash/Vector29.svg';
    }
  }

  hideBrokenImage(event: Event): void {
    const image = event.target as HTMLImageElement;
    if (image) image.style.display = 'none';
  }

  /**
   * @brief Obtiene el título visible de una alarma del panel.
   * @param {any} alarm - Registro de alarma recibido desde la API.
   * @returns {string} Título de la alarma.
   */
  getAlarmTitle(alarm: any): string {
    return _.get(alarm, 'AlarmType.name') || _.get(alarm, 'AlarmType.description') || alarm.description || alarm.name || 'Alarma activa';
  }

  /**
   * @brief Obtiene la ubicación asociada a una alarma.
   * @param {any} alarm - Registro de alarma recibido desde la API.
   * @returns {string} Nombre de la ubicación.
   */
  getAlarmLocation(alarm: any): string {
    return _.get(alarm, 'Device.Location.name') || _.get(alarm, 'Device.description') || 'Ubicación sin información';
  }

  /**
   * @brief Resuelve el tipo de activo desde Device.DeviceType.name.
   * @param {any} alarm - Alarma con su dispositivo relacionado.
   * @returns {string} Nombre del tipo de activo o "Sin información".
   */
  getAlarmAssetType(alarm: any): string {
    return alarm?.Device?.DeviceType?.name || 'Sin información';
  }

  /**
   * @brief Resuelve el sensor origen desde SourceDeviceSensorType.name.
   * @param {any} alarm - Alarma con la relación del sensor origen.
   * @returns {string} Nombre del sensor o "Sin información".
   */
  getAlarmSensorType(alarm: any): string {
    return alarm?.SourceDeviceSensorType?.name || 'Sin información';
  }

  /**
   * @brief Traduce la severidad de la alarma a la etiqueta del panel.
   * @param {any} alarm - Registro de alarma recibido desde la API.
   * @returns {string} Severidad visible para el usuario.
   */
  getAlarmSeverity(alarm: any): string {
    const level = String(alarm.level || alarm.severity || _.get(alarm, 'AlarmType.level') || '').toLocaleLowerCase();
    return level.includes('crit') || level.includes('alta') ? 'Crítica' : 'Activa';
  }

  private getAlarmLocationId(alarm: any): number {
    return Number(_.get(alarm, 'Device.location_id') || _.get(alarm, 'Device.Location.id'));
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.pendingTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
    this.pendingTimeouts.clear();
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.subscriptions = [];
    this.revokePhotoUrls();
    if (!this.embedded) document.body.classList.remove('siac-map-fullscreen-open');
  }

  private scheduleTimeout(callback: () => void, delay = 0): ReturnType<typeof setTimeout> {
    const timeoutId = setTimeout(() => {
      this.pendingTimeouts.delete(timeoutId);
      if (this.destroyed) return;
      callback();
    }, delay);
    this.pendingTimeouts.add(timeoutId);
    return timeoutId;
  }
}
